import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import PageWrapper from "components/PageWrapper";
import { observer } from "mobx-react";
import { useNavigate, useParams } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ReactNode, useEffect, useState } from "react";
import userRoleStore from "../api";
import PermissionsSection from "./permission-selection";
import { hasPermission, Permission } from "core/utils/roles";
import { useAuth } from "app/authHooks";
import loadingStore from "core/utils/loading";
import { PaginatedPermissionRequest, Permissions } from "../model";
import { enqueueSnackbar } from "notistack";

function AddEditUserRolesPermissions() {
  const { attributes } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<any[]>([]);
  const [userName, setUserName] = useState<string>();
  const [permissions, setPermissions] = useState<any>([]);
  const { loading } = loadingStore;
  const { getUserRoleById, getUserRolePermissions, updateUserRolePermissions } =
    userRoleStore;

  const canUpdateUserRole = hasPermission(
    "update:roles" as Permission
  );

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    const userRoles = await getUserRoleById(params?.id);
    setUserName(userRoles.name);
    setRoles(userRoles?.permissions || []);
    const userRolesPermissions = await getUserRolePermissions({
      pageIndex: 0,
      pageSize: 10000,
    } as PaginatedPermissionRequest);
    setPermissions(userRolesPermissions?.data || []);
  };

  const handleSubmitForm = async () => {
    const payload = {
      permissions: roles.map((p: Permissions) => ({
        name: p.name,
        entity: p.entity,
      })),
    } as any;
    const response = await updateUserRolePermissions(params.id, payload);
    if (response) {
      enqueueSnackbar(
        "User role permissions has been successfully updated!!!",
        { variant: "success" }
      );
      navigate("/security/roles");
    }
  };

  interface CardProps {
    title: ReactNode;
    cardContent: ReactNode;
  }

  const CardComponent: React.FC<CardProps> = ({ title, cardContent }) => {
    return (
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: "16px",
          width: "100%",
        }}
      >
        <header style={{ borderBottom: "1px solid #ddd", width: "100%" }}>
          {title}
        </header>
        <main style={{ marginTop: "20px" }}>{cardContent}</main>
      </div>
    );
  };

  return (
    <PageWrapper>
      <Box sx={{ py: 4, px: 4 }}>
        <Box
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "16px",
          }}
        >
          <Link underline="hover" onClick={() => navigate("/security/roles")}>
            <ArrowBackIcon
              sx={{ width: "16px", height: "16px", marginRight: "4px" }}
            />{" "}
            Back to User Role
          </Link>
        </Box>
      </Box>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmitForm();
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <CardComponent
            title={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="h3"
                  margin={"10px"}
                  sx={{ fontWeight: 700 }}
                >
                  {userName ?? "Update User Permissions"}
                </Typography>
              </Box>
            }
            cardContent={
              <PermissionsSection
                allPermissions={permissions}
                rolePermissions={roles}
                onChange={(perm, checked) => {
                  const updated = checked
                    ? [...roles, perm]
                    : roles.filter((p: any) => p.name !== perm.name);

                  setRoles(updated);
                }}
                disabled={!canUpdateUserRole}
              />
            }
          />
        </Box>

        <Button
          style={{ marginTop: 10 }}
          type="submit"
          color="primary"
          variant="contained"
          disabled={loading || !canUpdateUserRole}
          endIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "UPDATING" : "UPDATE"}
        </Button>
      </form>
    </PageWrapper>
  );
}

export default observer(AddEditUserRolesPermissions);
